import React, { useEffect, useRef } from 'react';

/**
 * Razorpay Payment Button embed.
 *
 * Razorpay ships a hosted, pre-configured checkout widget you paste into
 * any page as a <script> tag inside a <form>. When the script loads, it
 * fetches the button config (amount / description / branding) from the
 * Razorpay dashboard using the given ``buttonId`` and renders the button.
 *
 * Because React does not execute <script> tags that arrive through JSX
 * (it treats them as static text nodes), we inject the tag imperatively
 * via ``useEffect``. A module-level dedupe cache also guarantees we only
 * load the widget for a given buttonId ONCE per SPA session — repeatedly
 * mounting/unmounting the button (e.g. by opening & closing a modal) was
 * spamming Razorpay's endpoint and getting the whole session
 * rate-limited (``BAD_REQUEST_ERROR: Too many requests``).
 *
 * Docs:  https://razorpay.com/docs/payments/payment-button/
 */
// Module-level cache of forms that already have a payment-button.js
// script injected for a given buttonId. Prevents duplicate script loads
// even when React unmounts and remounts this component (e.g. through a
// modal that toggles its children).
const INJECTED_BUTTONS = new Set();

export function RazorpayPaymentButton({ buttonId, className = '', testId }) {
  const formRef = useRef(null);

  useEffect(() => {
    const form = formRef.current;
    if (!form || !buttonId) return undefined;

    // Defensive shim — Razorpay's widget internals reference a global
    // ``errorQueue`` array that isn't defined on first load, which
    // triggers a ReferenceError picked up by CRA's dev overlay. Harmless
    // in production but noisy in dev; pre-initialising the array
    // silences the warning without affecting the widget itself.
    if (typeof window !== 'undefined' && typeof window.errorQueue === 'undefined') {
      window.errorQueue = [];
    }

    // Guard against React 18 StrictMode's double-invoke of effects and
    // any parent re-mounts that would inject duplicate scripts.
    if (form.querySelector('script[data-payment_button_id]')) return undefined;
    // Same guard, at the session level — once we've injected the widget
    // for this buttonId anywhere in the SPA, do not inject it again.
    // Repeated injections trigger Razorpay's "Too many requests" 429.
    if (INJECTED_BUTTONS.has(buttonId)) return undefined;

    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    s.async = true;
    s.setAttribute('data-payment_button_id', buttonId);
    form.appendChild(s);
    INJECTED_BUTTONS.add(buttonId);

    // NOTE: We intentionally do *not* remove the script on unmount.
    // Razorpay's payment-button.js retains references to its own <script>
    // tag after load (for `document.currentScript` lookups, analytics
    // ping-backs, etc.). Removing it while the runtime is still live
    // triggers ``Cannot read properties of null (reading 'tagName')``
    // in production sourcemaps. Leaving the tag in place is safe
    // because we guard against duplicates via ``INJECTED_BUTTONS``.
    return undefined;
  }, [buttonId]);

  return (
    <form
      ref={formRef}
      className={className}
      data-testid={testId || `razorpay-payment-button-${buttonId}`}
    />
  );
}

export default RazorpayPaymentButton;
